/* ==========================================================================
   LAKSHIV ENTERPRISE — Wishlist, compare and recently viewed
   All three are localStorage-backed so they work for guests. When a customer
   is logged in the wishlist is namespaced by customer id, so switching
   accounts on a shared device never leaks saved items.
   ========================================================================== */

(function () {
  'use strict';

  var L = (window.Lakshiv = window.Lakshiv || {});
  var utils = L.utils;
  if (!utils) return;

  var customerId = document.body.getAttribute('data-customer-id') || 'guest';

  /* --- Storage helper ----------------------------------------------------- */

  function Store(key, limit) {
    this.key = key;
    this.limit = limit || 0;
  }

  Store.prototype.read = function () {
    try {
      var raw = window.localStorage.getItem(this.key);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      // Private browsing or a corrupted value — behave as if empty.
      return [];
    }
  };

  Store.prototype.write = function (items) {
    try {
      window.localStorage.setItem(this.key, JSON.stringify(items));
    } catch (e) {
      /* storage unavailable — the feature degrades to session-only */
    }
    return items;
  };

  Store.prototype.has = function (id) {
    return this.read().some(function (item) {
      return String(item.id) === String(id);
    });
  };

  Store.prototype.add = function (item) {
    var items = this.read().filter(function (existing) {
      return String(existing.id) !== String(item.id);
    });
    items.unshift(item);
    if (this.limit) items = items.slice(0, this.limit);
    return this.write(items);
  };

  Store.prototype.remove = function (id) {
    return this.write(
      this.read().filter(function (item) {
        return String(item.id) !== String(id);
      })
    );
  };

  Store.prototype.clear = function () {
    return this.write([]);
  };

  /* --- Wishlist ----------------------------------------------------------- */

  var wishlistStore = new Store('lakshiv:wishlist:' + customerId);

  var Wishlist = {
    all: function () {
      return wishlistStore.read();
    },

    has: function (id) {
      return wishlistStore.has(id);
    },

    add: function (item) {
      wishlistStore.add(item);
      this.sync();
      L.toast.show(L.strings.addedToWishlist, 'success');
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    },

    remove: function (id) {
      wishlistStore.remove(id);
      this.sync();
      L.toast.show(L.strings.removedFromWishlist, null, 1600);
      document.dispatchEvent(new CustomEvent('wishlist:updated'));
    },

    toggle: function (btn) {
      var id = btn.getAttribute('data-product-id');
      if (this.has(id)) {
        this.remove(id);
      } else {
        this.add({
          id: id,
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          url: btn.getAttribute('data-product-url'),
          image: btn.getAttribute('data-product-image'),
          price: btn.getAttribute('data-product-price'),
          variantId: btn.getAttribute('data-variant-id'),
          addedAt: Date.now()
        });
        btn.classList.add('is-animating');
        setTimeout(function () {
          btn.classList.remove('is-animating');
        }, 500);
      }
    },

    /** Reflects stored state onto every wishlist button and counter on screen. */
    sync: function (root) {
      var items = this.all();
      var ids = items.map(function (item) {
        return String(item.id);
      });

      utils.qsa('[data-wishlist-toggle]', root).forEach(function (btn) {
        var active = ids.indexOf(String(btn.getAttribute('data-product-id'))) !== -1;
        var label = active ? 'Remove from wishlist' : 'Add to wishlist';
        btn.setAttribute('aria-pressed', String(active));
        btn.setAttribute('title', label);
        btn.setAttribute('aria-label', label);
        var text = btn.querySelector('.wishlist-btn__label');
        if (text) text.textContent = active ? 'Saved' : 'Add to wishlist';
      });

      utils.qsa('[data-wishlist-count]').forEach(function (el) {
        el.textContent = items.length;
        el.hidden = items.length === 0;
      });
    }
  };

  L.wishlist = Wishlist;

  /* --- Compare ------------------------------------------------------------ */

  var COMPARE_LIMIT = 4;
  var compareStore = new Store('lakshiv:compare', COMPARE_LIMIT);

  var Compare = {
    all: function () {
      return compareStore.read();
    },

    toggle: function (btn) {
      var id = btn.getAttribute('data-product-id');
      if (compareStore.has(id)) {
        compareStore.remove(id);
      } else {
        if (compareStore.read().length >= COMPARE_LIMIT) {
          L.toast.show(L.strings.compareLimit, 'error');
          return;
        }
        compareStore.add({
          id: id,
          handle: btn.getAttribute('data-product-handle'),
          title: btn.getAttribute('data-product-title'),
          url: btn.getAttribute('data-product-url'),
          image: btn.getAttribute('data-product-image')
        });
      }
      this.sync();
      document.dispatchEvent(new CustomEvent('compare:updated'));
    },

    remove: function (id) {
      compareStore.remove(id);
      this.sync();
    },

    sync: function (root) {
      var items = this.all();
      var ids = items.map(function (item) {
        return String(item.id);
      });

      utils.qsa('[data-compare-toggle]', root).forEach(function (btn) {
        btn.setAttribute(
          'aria-pressed',
          String(ids.indexOf(String(btn.getAttribute('data-product-id'))) !== -1)
        );
      });

      var bar = utils.qs('[data-compare-bar]');
      if (!bar) return;

      var list = bar.querySelector('[data-compare-items]');
      bar.classList.toggle('is-visible', items.length > 0);

      if (!list) return;
      list.innerHTML = items
        .map(function (item) {
          return (
            '<div class="compare-bar__item">' +
            (item.image
              ? '<img src="' + item.image + '" alt="" width="52" height="52" loading="lazy">'
              : '') +
            '<button type="button" class="compare-bar__remove" data-compare-remove="' +
            item.id +
            '" aria-label="Remove">&times;</button></div>'
          );
        })
        .join('');

      var link = bar.querySelector('[data-compare-link]');
      if (link) {
        link.href =
          (L.routes.root || '/') +
          'pages/compare?ids=' +
          items
            .map(function (item) {
              return item.handle;
            })
            .join(',');
      }
    }
  };

  L.compare = Compare;

  /* --- Recently viewed ---------------------------------------------------- */

  var recentStore = new Store('lakshiv:recent', 12);

  var Recent = {
    all: function () {
      return recentStore.read();
    },

    track: function () {
      var meta = utils.qs('[data-recently-viewed-track]');
      if (!meta) return;
      recentStore.add({
        id: meta.getAttribute('data-product-id'),
        handle: meta.getAttribute('data-product-handle')
      });
    },

    /**
     * Renders the recently viewed rail using Shopify's search endpoint, so the
     * cards come back as real Liquid rather than being rebuilt in JS.
     */
    render: function () {
      var container = utils.qs('[data-recently-viewed]');
      if (!container) return;

      var currentHandle = container.getAttribute('data-exclude-handle');
      var handles = this.all()
        .map(function (item) {
          return item.handle;
        })
        .filter(function (handle) {
          return handle && handle !== currentHandle;
        })
        .slice(0, 8);

      if (!handles.length) {
        var emptySection = container.closest('[data-recently-viewed-section]');
        if (emptySection) emptySection.setAttribute('hidden', '');
        return;
      }

      var query = handles
        .map(function (handle) {
          return 'handle:' + handle;
        })
        .join(' OR ');

      var url =
        L.routes.search +
        '?type=product&options[unavailable_products]=last&q=' +
        encodeURIComponent(query) +
        '&section_id=product-card-results';

      fetch(url)
        .then(function (res) {
          if (!res.ok) throw new Error('Failed');
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var results = doc.querySelector('[data-recently-viewed-results]');
          if (!results || !results.children.length) {
            var section = container.closest('[data-recently-viewed-section]');
            if (section) section.setAttribute('hidden', '');
            return;
          }
          container.innerHTML = results.innerHTML;
          Wishlist.sync(container);
          if (L.observeReveal) L.observeReveal(container);
        })
        .catch(function () {
          var section = container.closest('[data-recently-viewed-section]');
          if (section) section.setAttribute('hidden', '');
        });
    }
  };

  L.recentlyViewed = Recent;

  /* --- Wishlist page ------------------------------------------------------ */

  function renderWishlistPage() {
    var container = utils.qs('[data-wishlist-grid]');
    if (!container) return;

    var empty = utils.qs('[data-wishlist-empty]');
    var items = Wishlist.all();

    if (!items.length) {
      container.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    var query = items
      .map(function (item) {
        return 'handle:' + item.handle;
      })
      .join(' OR ');

    container.innerHTML =
      '<div class="grid grid--products"><div class="skeleton" style="aspect-ratio:1"></div>' +
      '<div class="skeleton" style="aspect-ratio:1"></div></div>';

    fetch(
      L.routes.search +
        '?type=product&q=' +
        encodeURIComponent(query) +
        '&section_id=product-card-results'
    )
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var results = doc.querySelector('[data-recently-viewed-results]');
        container.innerHTML = results ? results.innerHTML : '';
        Wishlist.sync(container);
        if (L.observeReveal) L.observeReveal(container);
      })
      .catch(function () {
        container.innerHTML = '';
        if (empty) empty.hidden = false;
      });
  }

  /* --- Wiring -------------------------------------------------------------- */

  function init() {
    document.addEventListener('click', function (event) {
      var wishBtn = event.target.closest('[data-wishlist-toggle]');
      if (wishBtn) {
        event.preventDefault();
        Wishlist.toggle(wishBtn);
        return;
      }

      var compareBtn = event.target.closest('[data-compare-toggle]');
      if (compareBtn) {
        event.preventDefault();
        Compare.toggle(compareBtn);
        return;
      }

      var compareRemove = event.target.closest('[data-compare-remove]');
      if (compareRemove) {
        event.preventDefault();
        Compare.remove(compareRemove.getAttribute('data-compare-remove'));
      }
    });

    document.addEventListener('wishlist:updated', renderWishlistPage);

    // Re-apply state to markup injected by AJAX (filters, quick view, cart).
    document.addEventListener('lakshiv:section:loaded', function (event) {
      var root = (event.detail && event.detail.container) || document;
      Wishlist.sync(root);
      Compare.sync(root);
    });

    Wishlist.sync();
    Compare.sync();
    renderWishlistPage();

    if (L.settings.recentlyViewedEnabled) {
      Recent.track();
      Recent.render();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
